import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useTicketOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { PermissionsGate } from 'features/permissions'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

import { fieldLabelsForIds } from './fieldLabelsForIds'
import { AiAutofillBanner } from './GongAgentAutofill'
import { NiiBasicInformationCard } from './niiReport/cards/NiiBasicInformationCard'
import { NiiConclusionCard } from './niiReport/cards/NiiConclusionCard'
import { NiiInspectionEconomicsCard } from './niiReport/cards/NiiInspectionEconomicsCard'
import { NiiReportInfoCard } from './niiReport/cards/NiiReportInfoCard'
import { getInspectionRecommendationsSection } from './niiReport/inspectionRecommendations'
import {
  type NiiReportConfig,
  niiReportConfigSchema,
  type NiiReportFields,
} from './niiReport/niiReportApi'
import {
  NiiReportDataProvider,
  useNiiReportData,
} from './niiReport/NiiReportDataContext'
import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { NiiPdfExportButton } from './niiReport/pdf/NiiPdfExportButton'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const NiiReportContent: FC<{
  fields: NiiReportFields
  sections: FormSection[]
  zoneFieldName: string
  preScreening: NiiReportConfig['preScreening']
  readonly?: boolean
}> = ({ fields, sections, zoneFieldName, preScreening, readonly }) => {
  const stepId = useActiveStepId()
  const {
    hasUnsavedEditsOrSystemValues,
    hasUnsavedEdits,
    doSave,
    resetChanges,
    isLoading,
    applyAiLocalFieldValues,
    localValues,
  } = useNiiReportData()
  const { ontologyFields } = useTicketOntologyFields()
  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits: !readonly && hasUnsavedEdits,
    hasUnsavedEditsOrSystemValues: !readonly && hasUnsavedEditsOrSystemValues,
    save: doSave,
  })

  const recommendationsSection = useMemo(
    () => getInspectionRecommendationsSection(sections),
    [sections]
  )
  const agentSections = useMemo(
    () =>
      sections.filter(
        (s) =>
          s.agent !== null &&
          s.agent !== undefined &&
          s !== recommendationsSection
      ),
    [recommendationsSection, sections]
  )

  return (
    <Container>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasUnsavedEditsOrSystemValues}
            isLoading={isLoading}
            onReset={resetChanges}
            onSave={doSave}
          />
        </StickySaveBannerContainer>
      )}
      {!readonly &&
        agentSections.map((s) => (
          <AiAutofillBanner
            key={s.id}
            section={s}
            fieldValues={localValues}
            fieldLabels={fieldLabelsForIds(s.fields, ontologyFields)}
            onFieldValues={applyAiLocalFieldValues}
          />
        ))}
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.analysisDate }}>
        <NiiBasicInformationCard
          analysisDateField={fields.analysisDate}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.executiveSummary }}>
        <NiiReportInfoCard
          title='Executive Summary'
          fieldKey={fields.executiveSummary}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.internalComments }}>
        <NiiReportInfoCard
          title='Internal Assessment Comments and Notes'
          fieldKey={fields.internalComments}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.eligibility }}>
        <NiiConclusionCard
          eligibilityField={fields.eligibility}
          justificationField={fields.justification}
          recommendationsField={fields.recommendations}
          recommendationsSection={recommendationsSection}
          zoneFieldName={zoneFieldName}
          preScreening={preScreening}
          readonly={readonly}
        />
      </CommentIndicator>
      <PermissionsGate
        requires={[
          PERMISSION_GECKO_INTERNAL_MEMBER,
          PERMISSION_LIBRA_EXECUTIVE,
        ]}>
        <CommentIndicator
          anchor={{ type: 'field', stepId, fieldId: fields.inspectableArea }}>
          <NiiInspectionEconomicsCard
            areaField={fields.inspectableArea}
            readonly={readonly}
          />
        </CommentIndicator>
      </PermissionsGate>
      <NiiPdfExportButton fields={fields} zoneFieldName={zoneFieldName} />
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => niiReportConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, unknown>>
> = ({ config, readonly }) => {
  const { fields, sections, zoneFieldName, preScreening } = useMemo(
    () => niiReportConfigSchema.parse(config),
    [config]
  )

  return (
    <NiiReportDataProvider fields={fields}>
      <NiiReportContent
        fields={fields}
        sections={sections ?? []}
        zoneFieldName={zoneFieldName}
        preScreening={preScreening}
        readonly={readonly}
      />
    </NiiReportDataProvider>
  )
}
